import React from 'react';
import { Box, Typography, Button, Stack, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import Template1 from '../Template1/Template1';
import { mockRootProps } from '../Template1/Template1MockData';
import Template2 from '../Template2/Template2';
import { mockRootProps as template2MockData } from '../Template2/Template2MockData';
import Template3 from '../Template3/Template3';
import { mockRootProps as template3MockData } from '../Template3/Template3MockData';
import Template4 from '../Template4/Template4';
import { mockRootProps as template4MockData } from '../Template4/Template4MockData';
import Template5 from '../Template5/Template5';
import { mockRootProps as template5MockData } from '../Template5/Template5MockData';
import Template6 from '../Template6/Template6';
import { mockRootProps as template6MockData } from '../Template6/Template6MockData';
import Template7 from '../Template7/Template7';
import { template7MockData } from '../Template7/Template7MockData';
import Template8 from '../Template8/Template8';
import { template8MockData } from '../Template8/Template8MockData';

import logger from '../../utils/logger';
const PreviewOverlay = styled(Box)(({ theme, fullscreen }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  // Remove dim/shadow effect
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: fullscreen ? '0px' : theme.spacing(5),
  [theme.breakpoints.down('md')]: {
    padding: fullscreen ? '0px' : theme.spacing(4)
  },
  [theme.breakpoints.down('sm')]: {
    padding: fullscreen ? '0px' : theme.spacing(2)
  }
}));

const PreviewContainer = styled(Box)(({ theme, fullscreen }) => ({
  backgroundColor: '#ffffff',
  borderRadius: fullscreen ? '0px' : '12px',
  padding: '0px',
  paddingBottom: fullscreen ? 0 : theme.spacing(12),
  width: fullscreen ? '100vw' : 'min(95vw, 1200px)',
  height: fullscreen ? '100vh' : 'min(95vh, 860px)',
  overflow: 'auto',
  position: 'relative',
  boxShadow: fullscreen ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: fullscreen ? '0px' : '10px',
    paddingBottom: fullscreen ? 0 : theme.spacing(14)
  }
}));

const PreviewHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
}));

const PreviewTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#121927'
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  top: 16,
  right: 16,
  backgroundColor: '#EF232A',
  color: '#ffffff',
  borderRadius: '8px',
  width: 40,
  height: 40,
  zIndex: 1100, // Higher than template navigation (1000)
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  '&:hover': {
    backgroundColor: '#DC2626'
  }
}));

const PreviewImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  height: 'auto',
  borderRadius: '8px',
  // Remove borders/shadows for a clean look
  border: 'none'
}));

const PreviewActions = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '16px',
  position: 'fixed',
  bottom: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  justifyContent: 'center',
  zIndex: 1001,
  [theme.breakpoints.down('md')]: {
    gap: '12px'
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    bottom: '16px',
    width: 'calc(100% - 32px)',
    transform: 'translateX(-50%)',
    alignItems: 'stretch'
  }
}));

const ActionButton = styled(Button)(({ theme, color }) => ({
  backgroundColor: color || '#0baa68',
  color: '#ffffff',
  borderRadius: '8px',
  padding: '8px 16px',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: color ? `${color}DD` : '#00663c',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));



const TemplatePreview = ({ template, onClose, onUseTemplate, onViewLive, onChangeTemplate, onDeploy, fullscreen = false }) => {
  if (!template) return null;

  const renderTemplateContent = () => {
    // Check if we have real GMB data
    const hasRealData = template.realData && template.gmbData;

    logger.log('[TemplatePreview] Rendering template:', {
      templateId: template.id,
      hasRealData: hasRealData,
      realDataKeys: template.realData ? Object.keys(template.realData) : [],
      gmbDataPresent: !!template.gmbData
    });

    // Use ONLY real GMB data if available, otherwise show error message
    let templateData1, templateData2, templateData3, templateData4, templateData5, templateData6, templateData7, templateData8;

    if (hasRealData) {
      // Use real data for all templates
      templateData1 = template.realData;
      templateData2 = template.realData;
      templateData3 = template.realData;
      templateData4 = template.realData;
      templateData5 = template.realData;
      templateData6 = template.realData;
      templateData7 = template.realData;
      templateData8 = template.realData;

      logger.log('[TemplatePreview] Using real GMB data for template', template.id);
    } else {
      // Use mock data only as last resort
      logger.warn('[TemplatePreview] No real GMB data available, using mock data');
      templateData1 = mockRootProps;
      templateData2 = template2MockData;
      templateData3 = template3MockData;
      templateData4 = template4MockData;
      templateData5 = template5MockData;
      templateData6 = template6MockData;
      templateData7 = template7MockData;
      templateData8 = template8MockData;
    }

    // Show actual Template1 component for template ID 1
    if (template.id === 1) {
      return <Template1 templateData={templateData1} />;
    }

    // Show actual Template2 component for template ID 2
    if (template.id === 2) {
      return <Template2 templateData={templateData2} />;
    }

    // Show actual Template3 component for template ID 3
    if (template.id === 3) {
      return <Template3 templateData={templateData3} />;
    }

    // Show actual Template4 component for template ID 4
    if (template.id === 4) {
      return <Template4 templateData={templateData4} />;
    }

    // Show actual Template5 component for template ID 5
    if (template.id === 5) {
      return <Template5 templateData={templateData5} />;
    }

    // Show actual Template6 component for template ID 6
    if (template.id === 6) {
      return <Template6 templateData={templateData6} />;
    }

    // Show actual Template7 component for template ID 7
    if (template.id === 7) {
      return <Template7 templateData={templateData7} />;
    }

    // Show actual Template8 component for template ID 8
    if (template.id === 8) {
      return <Template8 templateData={templateData8} />;
    }

    // For other templates, show the image preview
    return (
      <PreviewImage
        src={template.image}
        alt={`Preview of ${template.name}`}
      />
    );
  };

  return (
    <PreviewOverlay onClick={onClose} fullscreen={fullscreen}>
      <PreviewContainer onClick={(e) => e.stopPropagation()} fullscreen={fullscreen}>
        <CloseButton aria-label="Close preview" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>

        {renderTemplateContent()}

        <PreviewActions>
          {onDeploy && template?.realData && template?.gmbData && (
            <ActionButton
              onClick={() => onDeploy(template)}
              color="#0baa68"
              data-tutorial="deploy-directory"
            >
              Post to GMB
            </ActionButton>
          )}
        </PreviewActions>
      </PreviewContainer>
    </PreviewOverlay>
  );
};

export default TemplatePreview;
